import { useCallback, useState } from 'react';
import { Button, Form, Link, Space } from 'tdesign-react';
import styled from 'styled-components';
import { ChevronUpDoubleIcon, SearchIcon } from 'tdesign-icons-react';
import { orderBy } from 'lodash';
import { useMemoizedFn } from 'ahooks';
import { produce } from 'immer';
import SvgIcon from '@/components/SvgIcon';
import { isVoid, sleep } from '@/utils/common';
import BuriedWrapper from '@/components/BuriedWrapper';

const defaultPageSize = 10;

// 表格排序用
export const getOrderData = (data, sort) => {
  if (!sort) {
    return data;
  }
  const keys = [];
  const orders = [];
  for (const item of [].concat(sort)) {
    keys.push(item.sortBy);
    orders.push(item.descending ? 'desc' : 'asc');
  }
  return orderBy(data, keys, orders);
};

// 分页配置,如force为true，则一定展示分页
export function getPageConfig(data = [], force) {
  if (data.length < 10 && !force) {
    return {
      defaultCurrent: 1,
      total: data.length,
      totalContent: true,
      showJumper: false,
      showPageNumber: false,
      showPageSize: false,
      showPreviousAndNextBtn: false,
    };
  }
  return {
    defaultCurrent: 1,
    total: data.length,
    totalContent: true,
    showJumper: true,
  };
}

/**
 * @typedef {null} typeEnum
 */

/**
 * @typedef {Object} Resp
 * @property {func} [setData] - 表格数据设置回调,如服务端分页，则需要传递第二个参数total
 * @property {any} [header] - 头部React节点，用于渲染
 * @property {any} [form] - 搜索框form实例
 * @property {any} [data] - 表格data
 * @property {any} [filter] - 缓存的搜索条件
 * @property {any} [config] - 表格配置
 * @property {func} [doRefresh] - 表格刷新
 * @property {func} [doSearch] - 手动刷新，传入values。如需其他表单值，第二个参数为true，values会覆盖表单字段
 */

/**
 * @typedef {import('tdesign-react/lib/table/type').BaseTableCol} TableCol
 */

/**
 * @typedef {import("react").ReactNode} ReactNode
 */

/**
 * @typedef {import('tdesign-react/lib/table/type').TableSort} TableSort
 */

/**
 * @typedef {import('tdesign-react/lib/table/type').RowClassNameParams } RowClassNameParams
 */

/**
 * @callback rowClassNameCb
 * @param {RowClassNameParams} params
 */

/**
 * Tdesign Table 常规配置hook，提供刷新、查询状态控制、排序、列控制等包装
 *
 * 无TS，不使用HOC，尽量保证原生代码提醒
 * @typedef {Object} Props
 * @prop {ReactNode=} search 搜索栏内容React片段，不带父节点Form
 * @prop {ReactNode=} searchExtra (高级搜索)内容React片段，不带父节点Form
 * @prop {boolean=} remote 是否远程分页，默认false
 * @prop {Function} onSearch 搜索回调，尽量使用 async func
 * @prop {Function=} onRefresh 刷新回调，尽量使用 async func，参数为缓存的筛选条件
 * @prop {Array<TableCol> } columns 表格columns属性, 额外添加defaultSort（默认排序'desc' | 'asc'，如有排序）， defaultHide（默认不展示）
 * @prop {ReactNode=} operators 操作按钮
 * @prop {typeEnum=} refresh 刷新按钮，null不显示
 * @prop {typeEnum=} columnController 列控制按钮，null不显示
 * @prop {typeEnum=} searchButton 搜索按钮，null不显示
 * @prop {ReactNode=} append 头部中间部分额外内容
 * @prop {string} searchEventId 搜索按钮埋点
 * @prop {boolean=} disableOptimize 是否禁用优化 如使用虚拟滚动，需要关闭此项
 * @prop {TableSort=} defaultSort 默认排序 如 [{ descending: true, sortBy: 'backlog' }]
 * @prop {rowClassNameCb} rowClassName 因为借助了rowClassName优化，在此处使用rowClassName
 * @prop {boolean=} noOptimize 是否禁用优化
 * @prop {boolean=} largeData 是否大量数据
 */

/**
 *
 * @param {Props} props
 * @returns {Resp}
 */
export default function useTable(props) {
  const {
    search,
    searchExtra,
    columns,
    onSearch,
    operators,
    append,
    defaultSort,
    refresh,
    columnController,
    searchButton,
    remote,
    searchEventId,
    onRefresh,
    rowClassName,
    noOptimize,
    largeData,
  } = props;
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState({}); // 搜索条件缓存
  const [sort, setSort] = useState(
    defaultSort ||
      columns
        .filter((item) => item.defaultSort)
        .map((item) => ({
          sortBy: item.colKey,
          descending: item.defaultSort === 'desc',
        })),
  ); // 默认排序，如有defaultSort优先，否则根据defaultSort字段 'desc' | 'asc'判断
  const [form] = Form.useForm();
  const [currentPage, setCurrentPage] = useState(1); // 当前页
  const [refreshIng, setRefreshIng] = useState(false);
  const [columnControllerVisible, setColumnControllerVisible] = useState(false);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [total, setTotal] = useState(0);
  const [displayColumns, setDisplayColumns] = useState(
    columns.filter((item) => !item.defaultHide).map((item) => item.colKey),
  );
  const [showExtraSearch, setShowExtraSearch] = useState(false);

  const _onSubmit = useMemoizedFn(async (values) => {
    setFilter(values);
    if (onSearch) {
      try {
        setLoading(true);
        const pageParams = {
          startIndex: currentPage,
          pageSize,
        };
        await onSearch(remote ? { ...values, ...pageParams } : values);
      } catch (error) {
        console.trace(error);
        throw error;
      } finally {
        setLoading(false);
      }
    }
  });

  // 简单等待state
  const onSubmit = useMemoizedFn(async (values) => {
    await sleep(50);
    return _onSubmit(values);
  });

  // 刷新操作
  const doRefresh = useMemoizedFn(async () => {
    try {
      setRefreshIng(true);
      setLoading(true);
      if (onRefresh) {
        await onRefresh(filter);
      } else {
        await onSubmit(filter);
      }
    } catch (error) {
      console.trace(error);
      throw error;
    } finally {
      setRefreshIng(false);
      setLoading(false);
    }
  });

  const handleSubmit = ({ validateResult, fields }) => {
    if (validateResult === true) {
      setCurrentPage(1);
      onSubmit(fields);
    }
  };

  const header = (
    <Form form={form} onSubmit={handleSubmit} resetType='initial'>
      <SearchContainer>
        <BasicSearch className='search-form'>
          {search && (
            <FormDiv className='t-form t-form-inline'>
              {search}
              {searchButton !== null && (
                <BuriedWrapper eventId={searchEventId}>
                  <Button
                    loading={loading}
                    variant='outline'
                    style={{ marginLeft: 10 }}
                    onClick={() => form.submit()}
                    icon={<SearchIcon />}
                  >
                    搜索
                  </Button>
                </BuriedWrapper>
              )}
              {searchExtra && (
                <MoreButton
                  suffixIcon={<ChevronUpDoubleIcon size='16' />}
                  $rotate={showExtraSearch}
                  theme='primary'
                  hover='color'
                  style={{ height: 32, marginLeft: 18 }}
                  onClick={() => setShowExtraSearch((visible) => !visible)}
                >
                  高级搜索
                </MoreButton>
              )}
            </FormDiv>
          )}
          {append}
          <Space size={10}>
            {refresh !== null && (
              <IconBlock onClick={doRefresh}>
                <Icon type='refresh' className={refreshIng ? 't-icon-loading' : null} />
              </IconBlock>
            )}

            {columnController !== null && (
              <IconBlock onClick={() => setColumnControllerVisible(true)}>
                <Icon type='filter' />
              </IconBlock>
            )}
            {operators}
          </Space>
        </BasicSearch>

        {showExtraSearch && (
          <MoreSearch className='t-form t-form-inline search-form-extra'>
            {searchExtra}
            <div style={{ flex: 1 }} />
            <MoreRight>
              <Space size='small'>
                <Button theme='default' onClick={() => form.reset()} variant='outline'>
                  重置
                </Button>
                <Button
                  theme='primary'
                  icon={<SearchIcon />}
                  loading={loading}
                  onClick={() => form.submit()}
                >
                  搜索
                </Button>
              </Space>
            </MoreRight>
          </MoreSearch>
        )}
      </SearchContainer>
    </Form>
  );

  const onCurrentChange = (page) => {
    setCurrentPage(page);
    if (remote) {
      onSubmit(filter);
    }
  };

  const onPageSizeChange = (size) => {
    setPageSize(size);
    if (remote) {
      onSubmit(filter);
    }
  };

  const pageConfig = getPageConfig(data, remote);
  delete pageConfig.defaultCurrent;
  if (remote) {
    pageConfig.total = total;
  }

  /**
   * 进行搜索操作
   *
   * @param {Object} values - 搜索条件
   * @param {boolean} withFormValues - 是否将表单的值也传递给搜索操作，默认为 false
   */
  const doSearch = useCallback(
    (values, withFormValues) => {
      if (withFormValues) {
        onSubmit({ ...form.getFieldsValue(true), ...values });
      } else {
        onSubmit(values);
      }
      setCurrentPage(1);
      setPageSize(defaultPageSize);
    },
    [onSubmit, form],
  );

  // 性能优化columns（数量级 数千行）,Tdesign 本身问题,无用cell回调直接返回null
  // 如用到虚拟滚动（数量级 数万行+），需要移除该优化
  const optimizeColumns = (_columns) =>
    produce(_columns, (draft) => {
      draft.forEach((item) => {
        const cellCb = item.cell;
        if (typeof cellCb === 'function' && !isVoid(cellCb)) {
          item.cell = (...args) => {
            const { rowIndex } = args[0];
            if (rowIndex < pageSize) {
              return cellCb(...args);
            }
            return null;
          };
        }
      });
    });

  // 超2000行，启用大数据模式
  const isLargeData = largeData || data.length > 2000;

  return {
    /**
     *
     * @param {Array<*>} data 数据
     * @param {number} total 总数量（如服务端分页则要传）
     */
    setData: useMemoizedFn((data, total) => {
      setData(data);
      setTotal(total);
    }),
    header,
    form,
    data,
    filter,
    doRefresh,
    doSearch,
    config: {
      resizable: true,
      loading,
      columns: isLargeData || noOptimize ? columns : optimizeColumns(columns),
      displayColumns,
      onDisplayColumnsChange: setDisplayColumns,
      columnControllerVisible,
      onColumnControllerVisibleChange: setColumnControllerVisible,
      columnController: {
        dialogProps: { preventScrollThrough: true },
        hideTriggerButton: true,
      },
      maxHeight: isLargeData ? 'calc(100vh - 360px)' : 1000, // 暂不处理，仅头部样式需要
      pagination: isLargeData
        ? false
        : {
            ...pageConfig,
            current: currentPage,
            onCurrentChange,
            onPageSizeChange,
            pageSize,
          },
      scroll: isLargeData ? { type: 'virtual', threshold: 1 } : null,
      showSortColumnBgColor: true,
      multipleSort: true,
      sort,
      onSortChange: setSort,
      data: getOrderData(data, sort),
      rowClassName: (params) => {
        if (isLargeData) return;

        const { rowIndex } = params;
        const clx = [];
        // hack: 特殊闪跳优化，性能优化，Tdesign table本身问题，data瞬间全部渲染问题, 借助css优化
        const optimizeClx = rowIndex >= pageSize && 'optimize-hide';
        if (!noOptimize && optimizeClx) {
          clx.push(optimizeClx);
        }
        if (rowClassName) {
          let rowClass = '';
          if (typeof rowClassName === 'function') {
            rowClass = rowClassName(params);
          }
          if (typeof rowClassName === 'string') {
            rowClass = rowClassName;
          }
          if (rowClass) {
            clx.push(rowClass);
          }
        }
        return clx.join(' ');
      },
    },
  };
}

const SearchContainer = styled.div`
  .t-form-inline .t-form__item {
    margin-right: var(--td-comp-margin-s);
  }
  .t-date-picker {
    flex: 1;
  }
`;

const MoreSearch = styled.div`
  border-radius: 12px;
  background: #f7f8fa;
  padding: 20px;
  margin-bottom: 20px;
  display: flex;
`;

const MoreRight = styled.div`
  display: flex;
  align-items: flex-end;
`;

const MoreButton = styled(Link)`
  .t-link__suffix-icon {
    margin-left: 0;
  }
  .t-icon {
    margin-top: -3px;
    transition: 0.2s all cubic-bezier(0.645, 0.045, 0.355, 1);
    transform: rotate(${(props) => (props.$rotate ? 0 : 180)}deg);
  }
`;

const FormDiv = styled.div`
  flex: 1;
`;

const BasicSearch = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  margin-bottom: 20px;
`;

const IconBlock = styled.div`
  width: 32px;
  height: 32px;
  background: #ffffff;

  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const Icon = styled(SvgIcon)`
  font-size: 16px;
  color: var(--td-font-gray-2);
`;
